/**
 * F-047: React Query hook for the admin incoming ordered-orders count badge.
 *
 * Polls every 60s while tab is visible; pauses when backgrounded.
 * Refetches on window focus for immediate freshness when merchant returns.
 * Internally manages document.title as a non-visual side effect.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { countOrdersNeedingAttentionForUser, useTranslation } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

export interface UseIncomingOrdersCountResult {
  /** Raw numeric count. 0 when disabled (userId null) or on error. */
  count: number;
  /** Display string: exact number for 0-99, '99+' for >= 100. */
  displayCount: string;
  isLoading: boolean;
  isError: boolean;
}

export function useIncomingOrdersCount(userId: string | null): UseIncomingOrdersCountResult {
  const { t, lang } = useTranslation();

  const query = useQuery({
    queryKey: ['incoming-orders-count-user', userId] as const,
    enabled: !!userId,
    queryFn: () => countOrdersNeedingAttentionForUser(supabase, userId as string),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const count = query.data ?? 0;
  const displayCount = count >= 100 ? '99+' : String(count);

  useEffect(() => {
    document.title =
      count > 0 ? t('admin.shell.tabTitle', { count }) : t('admin.shell.tabTitleDefault');
  }, [count, lang, t]);

  return {
    count,
    displayCount,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
